import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { copilotPanelWidth } from '@careerhub/common';

export const useChatBladeStyles = makeStyles({
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    ...shorthands.padding(0),
    ...shorthands.margin('-1px'),
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    ...shorthands.border(0),
  },
  provider: {
    maxWidth: copilotPanelWidth,
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    columnGap: '24px',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    '@media (max-width: 639px)': {
      maxWidth: '100%',
    },
  },

  textAreaPopover: {
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    width: '100%',
  },
  textAreaPopoverSpacing: {
    marginTop: '8px',
  },

  buttonColor: {
    ':hover': { color: '#387A26' },
  },

  latencyWrapper: {
    paddingTop: '16px',
    alignItems: 'stretch',
  },

  /** Icon and title */
  headerLeft: {
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    boxSizing: 'border-box',
    columnGap: '8px',
  },

  chat: {
    overflowY: 'scroll',
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
    ...shorthands.padding('16px'),
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: tokens.colorNeutralForeground4,
      ...shorthands.border('2px', 'solid', tokens.colorNeutralBackground3),
      ...shorthands.borderRadius(tokens.borderRadiusMedium),
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
    '&::-webkit-scrollbar': {
      width: tokens.spacingHorizontalS,
    },
  },

  chatMessage: {
    display: 'block',
    marginLeft: 0,
  },

  // Includes reference links (not reference cards)
  chatMessageBody: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    boxSizing: 'content-box',
    display: 'block',
    maxWidth: '100%',
    whiteSpace: 'pre-wrap',
    '@media (max-width: 320px)': {
      maxWidth: '280px',
    },
  },

  // Includes reference cards (not reference links)
  botMessageBody: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    boxSizing: 'content-box',
    maxWidth: '100%',
    whiteSpace: 'pre-wrap',
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
    '@media (max-width: 320px)': {
      maxWidth: '280px',
    },
  },

  bookPopOverContainer: {
    display: 'flex',
    width: '100%',
  },

  initialMessage: {
    '@media (max-width: 320px)': {
      maxWidth: '280px',
    },
  },
  startOverButton: {
    maxWidth: 'fit-content',
    textAlign: 'center',
    fontSize: '18px',
    ':hover': { color: '#387A26' },
  },

  suggestionHeader: {
    height: '32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },

  popoverSurface: {
    ...shorthands.padding('8px', '8px', '8px', '8px'),
    maxHeight: '50vh',
    overflowY: 'auto',
    '@media (max-width: 320px)': {
      height: '100px',
    },
  },

  popoverTitle: {
    color: tokens.colorNeutralForeground3,
    paddingLeft: '8px',
  },

  suggestion: {
    display: 'flex',
    minHeight: '32px',
    alignItems: 'center',
    alignSelf: 'stretch',
    cursor: 'pointer',
    fontSize: '12px',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  promptExample: {
    ...shorthands.border('none'),
    display: 'flex',
    justifyContent: 'flex-start',
    textAlign: 'left',
    fontWeight: 400,
    width: '100%',
    height: 'auto',
    whiteSpace: 'normal',
    '& .fui-Button__content': {
      display: 'inline',
      width: '100%',
      textAlign: 'left',
      fontWeight: 400,
      whiteSpace: 'normal',
      overflowWrap: 'break-word',
    },
    ':hover': {
      width: '100%',
    },
  },

  promptExampleHighlight: {
    display: 'inline-block',
    backgroundColor: tokens.colorNeutralBackground2Pressed,
    paddingLeft: '3px',
    paddingRight: '3px',
    marginLeft: '2px',
    marginRight: '2px',
    whiteSpace: 'nowrap',
  },
  referenceGroup: {
    marginTop: '12px',
  },

  chatMyMessage: {
    gridTemplateAreas: 'unset',
    marginLeft: 0,
  },

  chatMyMessageBody: {
    backgroundColor: '#E0E7FF',
  },

  inputArea: {
    ...shorthands.padding('16px'),
  },

  card: {
    rowGap: tokens.spacingHorizontalM,
  },

  prompts: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingHorizontalS,
  },

  promptHighlight: {
    color: tokens.colorBrandForegroundLink,
  },

  suggestionsPopover: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    listStyleType: 'none',
    ...shorthands.margin('0px'),
    ...shorthands.padding('0px'),
    '& > li': {
      width: '100%',
    },
    '@media (max-width: 320px)': {
      rowGap: '0px',
    },
  },

  latency: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    ...shorthands.gap('8px'),
  },

  surfaceContainer: {
    overflowY: 'auto',
  },

  feedbackContainer: {
    display: 'flex',
    flexDirection: 'column',
  },

  feedbackButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    ...shorthands.gap('8px'),
  },

  dismissButtonContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },

  feedbackTextBox: {
    width: '100%',
    marginTop: '8px',
    marginBottom: '8px',
    ':focus-within': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '2px',
    },
  },

  feedbackFineText: {
    marginBottom: '24px',
  },

  checkboxContainer: {
    marginTop: '8px',
    marginBottom: '12px',
    display: 'flex',
    flexDirection: 'column',
  },
});
